import { ActionConfirmationHost, usePublishActionOutcome } from "./ui/ActionConfirmation.js";
import { useState, type FormEvent } from "react";
import {
  getMyDbs,
  saveMyDbs,
  setMyDbsCheckConsent,
  type DbsCheckConsent,
  type DbsInput,
  type DbsLevel,
  type DbsStatus,
  type DbsView,
} from "./api.js";
import { DateField } from "./calendar/DateField.js";
import { isDayKey } from "./calendar/grid.js";
import { formatDate } from "./format.js";
import { useAsync } from "./useAsync.js";
import { TableScrollRegion } from "./ui/TableScrollRegion.js";

/**
 * A tutor's own DBS.
 *
 * Theirs to enter and correct; nobody else's to see. What it says on screen is
 * deliberately about what to do next rather than about the record — somebody
 * looking at this wants to know whether they are holding the school up, and if
 * so by what.
 */

export const DBS_LABEL: Record<DbsStatus, string> = {
  missing: "nothing recorded",
  unverified: "not yet verified",
  "not-subscribed": "not on the Update Service",
  lapsed: "subscription lapsed",
  "renewal-due": "renewal due",
  valid: "valid",
};

/** Which statuses are a problem rather than a note. */
export const DBS_IS_PROBLEM: Record<DbsStatus, boolean> = {
  missing: true,
  unverified: false,
  "not-subscribed": true,
  lapsed: true,
  "renewal-due": false,
  valid: false,
};

const LEVELS: Array<{ value: DbsLevel; label: string }> = [
  { value: "enhanced-barred", label: "Enhanced, with barred list" },
  { value: "enhanced", label: "Enhanced" },
  { value: "standard", label: "Standard" },
  { value: "basic", label: "Basic" },
];

export function DbsStatusTag({ status }: { status: DbsStatus }) {
  if (status === "valid") return <span className="tag">valid</span>;
  return (
    <span className={DBS_IS_PROBLEM[status] ? "tag warn" : "tag"}>
      {DBS_LABEL[status]}
    </span>
  );
}

/** What this account should do about it, if anything. */
function whatToDo(dbs: DbsView | null): string {
  if (dbs === null) {
    return "The school needs a current DBS on file for everybody who teaches. Add yours below.";
  }
  switch (dbs.status) {
    case "unverified":
      return "Recorded. Somebody at the school has to see the original certificate before it counts — bring it in or show them a photo of it.";
    case "not-subscribed":
      return "Without an Update Service subscription this certificate is only a snapshot of the day it was issued. Subscribe within 30 days of that date, or a new DBS will be needed.";
    case "lapsed":
      return "The Update Service subscription has run out. It cannot be restarted — a lapsed subscription means applying for a whole new DBS, so tell the school now.";
    case "renewal-due":
      return "Renew the Update Service subscription before the date below. Letting it lapse means a whole new DBS, which costs money and takes weeks.";
    case "valid":
      return "Nothing to do.";
    default:
      return "";
  }
}

/**
 * "May we check you on the Update Service?", and their answer.
 *
 * Its own block above the certificate rather than a row inside the table,
 * because it is the tutor's to change at any time and the rest of that table is
 * a record of what they and the school have already established.
 *
 * Never asked and answered-no read differently on purpose. A tutor who has
 * never been asked has nothing to correct; one who declined has made a decision
 * the school can see and talk to them about, which is the entire reason the
 * column has three states rather than two.
 */
function CheckConsent({
  consent,
  onSaved,
}: {
  consent: DbsCheckConsent;
  onSaved: () => void;
}) {
  const publish = usePublishActionOutcome();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function answer(given: boolean) {
    setBusy(true);
    setError(null);
    try {
      await setMyDbsCheckConsent(given);
      publish({ variant: "success", title: "DBS check consent recorded", details: [{ value: given ? "Consent was given for Update Service checks." : "Consent was withdrawn for Update Service checks." }] });
      onSaved();
    } catch (cause: unknown) {
      setError(cause instanceof Error ? cause.message : "Could not save that.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="panel soft" style={{ margin: "0 0 12px" }}>
      {error && <div className="error small">{error}</div>}

      <label
        className="row small"
        style={{ gap: 8, alignItems: "flex-start", margin: 0 }}
      >
        <input
          type="checkbox"
          checked={consent.given === true}
          disabled={busy}
          onChange={(e) => void answer(e.target.checked)}
        />
        <span>
          Wirral Music Factory may check my status on the DBS Update Service.
          <span className="muted">
            {" "}
            {consent.given === null
              ? "You have not been asked this before."
              : consent.given
                ? `Agreed${consent.at ? ` on ${formatDate(consent.at)}` : ""}.`
                : `Declined${consent.at ? ` on ${formatDate(consent.at)}` : ""} — the school cannot check you until this changes.`}{" "}
            This is separate from the school keeping a record of your DBS, which
            it does because it teaches children. Changing it here removes
            nothing.
          </span>
        </span>
      </label>
    </div>
  );
}

export function MyDbs() {
  return <ActionConfirmationHost><MyDbsContent /></ActionConfirmationHost>;
}

function MyDbsContent() {
  const { data, error, loading, reload } = useAsync(() => getMyDbs(), []);
  const [editing, setEditing] = useState(false);

  if (loading) return null;
  if (error) return <div className="error">{error}</div>;

  const dbs = data?.dbs ?? null;

  return (
    <div className="panel">
      <h2>Your DBS</h2>

      {!editing && (
        <>
          <p className="small muted" style={{ margin: "0 0 12px" }}>
            {whatToDo(dbs)}
          </p>

          <CheckConsent
            consent={data?.consent ?? { given: null, at: null }}
            onSaved={reload}
          />

          {dbs && (
            <TableScrollRegion label="Your DBS record" minWidth="420px">
              <table>
                <tbody>
                  <tr>
                    <td className="muted small">Status</td>
                    <td>
                      <DbsStatusTag status={dbs.status} />
                    </td>
                  </tr>
                  <tr>
                    <td className="muted small">Certificate</td>
                    <td className="small">
                      {dbs.certificateNumber} · issued{" "}
                      {formatDate(`${dbs.issuedOn}T12:00:00Z`)}
                    </td>
                  </tr>
                  <tr>
                    <td className="muted small">Update Service</td>
                    <td className="small">
                      {dbs.updateServiceSubscribed && dbs.updateServiceRenewsOn
                        ? `subscribed, renews ${formatDate(`${dbs.updateServiceRenewsOn}T12:00:00Z`)}`
                        : "not subscribed"}
                    </td>
                  </tr>
                  <tr>
                    <td className="muted small">Seen by</td>
                    <td className="small">
                      {dbs.verifiedAt
                        ? `${dbs.verifiedBy ?? "the school"} on ${formatDate(dbs.verifiedAt)}`
                        : "nobody yet"}
                    </td>
                  </tr>
                </tbody>
              </table>
            </TableScrollRegion>
          )}

          <button
            className="quiet"
            type="button"
            onClick={() => setEditing(true)}
          >
            {dbs ? "Update it" : "Add your DBS"}
          </button>
        </>
      )}

      {editing && (
        <DbsForm
          current={dbs}
          onCancel={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            reload();
          }}
        />
      )}
    </div>
  );
}

function DbsForm({
  current,
  onCancel,
  onSaved,
}: {
  current: DbsView | null;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const publish = usePublishActionOutcome();
  const [form, setForm] = useState<DbsInput>({
    certificateNumber: current?.certificateNumber ?? "",
    issuedOn: current?.issuedOn ?? "",
    level: current?.level ?? "enhanced-barred",
    updateServiceSubscribed: current?.updateServiceSubscribed ?? false,
    updateServiceRenewsOn: current?.updateServiceRenewsOn ?? null,
  });
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();

    /* Checked here rather than by the browser. `required` on an
       `<input type="date">` was doing this, and the field is no longer one —
       so the rule it enforced is written down instead of lost, and says which
       date is missing rather than pointing at a box. */
    if (!isDayKey(form.issuedOn)) {
      setProblem("Please give the date the certificate was issued.");
      return;
    }
    if (
      form.updateServiceSubscribed &&
      !isDayKey(form.updateServiceRenewsOn ?? "")
    ) {
      setProblem(
        "Please give the date the Update Service subscription renews.",
      );
      return;
    }

    setBusy(true);
    setProblem(null);
    try {
      await saveMyDbs(form);
      publish({ variant: "success", title: "DBS details saved", details: [{ value: "The certificate details were recorded. This does not verify the certificate or perform an Update Service check." }] });
      onSaved();
    } catch (cause: unknown) {
      setProblem(
        cause instanceof Error ? cause.message : "Could not save that.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit}>
      {/* Said before they type, because it is the reassuring part and the
          thing people ask: the school keeps the reference, not the contents. */}
      <p className="small muted">
        Only these details are kept — the number, the date and whether you are
        on the Update Service. Nothing the certificate disclosed is stored, and
        no copy of it is kept. Only the school owner can see this; other tutors
        cannot.
      </p>

      {problem && <div className="error small">{problem}</div>}
      {current?.verifiedAt && (
        <p className="small muted">
          Changing the number, the date or the level means it has to be seen
          again — the verification is cleared.
        </p>
      )}

      <div className="fields">
        <label>
          <span className="small muted">Certificate number</span>
          <input
            value={form.certificateNumber}
            onChange={(e) =>
              setForm({ ...form, certificateNumber: e.target.value })
            }
            placeholder="12 digits"
            inputMode="numeric"
            required
          />
        </label>

        <DateField
          label="Issued on"
          onChange={(issuedOn) => setForm({ ...form, issuedOn })}
          value={form.issuedOn}
        />

        <label>
          <span className="small muted">Level</span>
          <select
            value={form.level}
            onChange={(e) =>
              setForm({ ...form, level: e.target.value as DbsLevel })
            }
          >
            {LEVELS.map((level) => (
              <option key={level.value} value={level.value}>
                {level.label}
              </option>
            ))}
          </select>
        </label>

        <label>
          <span className="small muted">On the Update Service</span>
          <input
            type="checkbox"
            checked={form.updateServiceSubscribed}
            onChange={(e) =>
              setForm({
                ...form,
                updateServiceSubscribed: e.target.checked,
                // Clearing the flag clears the date with it: a renewal date for
                // a subscription that does not exist is a fact about nothing.
                updateServiceRenewsOn: e.target.checked
                  ? form.updateServiceRenewsOn
                  : null,
              })
            }
          />
        </label>

        {form.updateServiceSubscribed && (
          <DateField
            label="Subscription renews"
            onChange={(renews) =>
              setForm({ ...form, updateServiceRenewsOn: renews || null })
            }
            value={form.updateServiceRenewsOn ?? ""}
          />
        )}
      </div>

      <div className="row">
        <button type="submit" disabled={busy}>
          {busy ? "Saving…" : "Save"}
        </button>
        <button className="quiet" type="button" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}
